import { useState, useEffect } from "react";
import { ArrowRight, Car, Plane, MapPin, Shield, Clock, IndianRupee, Star } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BookingForm from "@/components/BookingForm";
import WhatsAppButton from "@/components/WhatsAppButton";

const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1920&q=80",
    caption: "Highways that call to you",
  },
  {
    url: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1920&q=80",
    caption: "Winding mountain roads",
  },
  {
    url: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1920&q=80",
    caption: "Statue of Unity, Kevadia",
  },
  {
    url: "https://images.unsplash.com/photo-1470770841072-f978cf4d019e?auto=format&fit=crop&w=1920&q=80",
    caption: "Sahyadri hills, near Saputara",
  },
];
const SERVICES = [
  { icon: Car, title: "Local Rides", desc: "Hourly packages within the city — errands, shopping, meetings.", tag: "4hr / 8hr / 12hr" },
  { icon: MapPin, title: "Outstation Trips", desc: "One-way and round-trip to any city across India, with experienced drivers.", tag: "One-way • Round trip" },
  { icon: Plane, title: "Airport Transfer", desc: "Punctual pickups & drops, flight-tracking, meet-and-greet available.", tag: "24×7 available" },
];

const FLEET = [
  { name: "Sedan", capacity: "4 seats · AC", img: "https://images.pexels.com/photos/11877375/pexels-photo-11877375.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "SUV", capacity: "6-7 seats · AC", img: "https://images.pexels.com/photos/15188438/pexels-photo-15188438.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Tempo Traveller", capacity: "10-14 seats · AC", img: "https://images.unsplash.com/photo-1473655587843-eda8944061e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHBhc3NlbmdlciUyMHRyYXZlbHxlbnwwfHx8fDE3ODUwODA0MjN8MA&ixlib=rb-4.1.0&q=85" },
];

const ROUTES = [
  { to: "Mumbai", time: "5h", km: "285 km" },
  { to: "Ahmedabad", time: "4h", km: "265 km" },
  { to: "Vadodara", time: "2h", km: "150 km" },
  { to: "Bharuch", time: "1.5h", km: "80 km" },
  { to: "Statue of Unity (Kevadia)", time: "3.5h", km: "220 km" },
  { to: "Saputara", time: "4h", km: "160 km" },
  { to: "Daman", time: "2h", km: "110 km" },
  { to: "Rajasthan (Udaipur)", time: "7h", km: "500 km" },
];

const TRUST = [
  { icon: Shield, title: "Verified Drivers", desc: "Every driver background-checked & trained." },
  { icon: Clock, title: "On-time, Always", desc: "Punctual pickups, tracked in real-time." },
  { icon: IndianRupee, title: "Transparent Fares", desc: "No hidden charges. Toll & parking billed at actuals." },
  { icon: Star, title: "Rated 4.9/5", desc: "By 5,000+ happy travellers this year." },
];

const Home = () => {
  const [prefill, setPrefill] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, 6500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // preload subsequent images
    HERO_IMAGES.forEach((h) => {
      const img = new Image();
      img.src = h.url;
    });
  }, []);

  const bookRoute = (destination) => {
    setPrefill({ tripType: "outstation", pickup: "Surat", drop: destination, _ts: Date.now() });
    setTimeout(() => {
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--brand-forest-dark)]">
        <div className="absolute inset-0 -z-10">
          {HERO_IMAGES.map((h, i) => (
            <img
              key={h.url}
              src={h.url}
              alt={h.caption}
              data-testid={`hero-image-${i}`}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-in-out ${
                i === heroIdx ? "opacity-40 ken-burns" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--brand-forest-dark)] via-[color:var(--brand-forest)]/90 to-[color:var(--brand-ink)]" />
          <div className="absolute inset-0 bg-black/35" />
          <div className="noise-overlay" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6 text-white fade-up">
            <span className="inline-block text-xs tracking-[0.3em] uppercase bg-white/20 border border-white/25 backdrop-blur px-3 py-1 rounded-full text-white">Travels &amp; Infra · Since 2010</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] text-white [text-shadow:0_2px_18px_rgba(0,0,0,0.45)]">
              Every road is a story. <em className="not-italic text-[color:var(--brand-amber)]">Yours begins here.</em>
            </h1>
            <p className="mt-6 text-lg text-white max-w-xl leading-relaxed [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]">
              Bawajii Travels &amp; Infra — trusted rides for local drives, weekend getaways, and airport transfers.
              Book online, pay after the ride.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#book" data-testid="hero-book-btn" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-amber)] text-white px-6 py-3 font-semibold hover:bg-amber-700 transition-colors duration-300">
                Book your ride <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#fleet" data-testid="hero-fleet-btn" className="inline-flex items-center gap-2 rounded-full border border-white/60 text-white px-6 py-3 font-medium hover:bg-white hover:text-[color:var(--brand-ink)] transition-colors duration-300">
                Explore fleet
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="font-display text-3xl text-white">15+</div><div className="text-xs uppercase tracking-widest text-white/80">Years</div></div>
              <div><div className="font-display text-3xl text-white">50K+</div><div className="text-xs uppercase tracking-widest text-white/80">Rides</div></div>
              <div><div className="font-display text-3xl text-white">4.9★</div><div className="text-xs uppercase tracking-widest text-white/80">Rating</div></div>
            </div>
          </div>

          <div id="book" className="lg:col-span-6 fade-up" style={{ animationDelay: "0.15s" }}>
            <BookingForm prefill={prefill} />
          </div>
        </div>

        {/* Hero image caption + dots */}
        <div className="absolute bottom-4 left-0 right-0 z-10 flex items-center justify-center gap-3 px-6" data-testid="hero-dots">
          <span className="hidden md:inline text-[11px] uppercase tracking-widest text-white/70">
            {HERO_IMAGES[heroIdx].caption}
          </span>
          <div className="flex items-center gap-2">
            {HERO_IMAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show hero image ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => setHeroIdx(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === heroIdx ? "w-8 bg-[color:var(--brand-amber)]" : "w-3 bg-white/45 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* MOTTO BAR */}
      <section aria-label="Our promise" className="bg-[color:var(--brand-forest)] text-white" data-testid="motto-bar">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex flex-wrap items-center justify-center gap-6 md:gap-16">
          <span className="font-display text-lg md:text-2xl tracking-wide">Comfortable</span>
          <span className="text-[color:var(--brand-amber)] text-2xl leading-none">|</span>
          <span className="font-display text-lg md:text-2xl tracking-wide">Reasonable</span>
          <span className="text-[color:var(--brand-amber)] text-2xl leading-none">|</span>
          <span className="font-display text-lg md:text-2xl tracking-wide">Reliable</span>
        </div>
      </section>

      {/* ABOUT US */}
      <section id="about" className="mx-auto max-w-7xl px-6 lg:px-10 py-24" data-testid="about-section">
        <div className="grid gap-12 md:grid-cols-12 items-start">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--brand-amber)]">About us</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              A Surat-born travel &amp; infra company built on <em className="not-italic text-[color:var(--brand-forest)]">trust</em>.
            </h2>
            <div className="mt-6 space-y-5 text-[color:var(--brand-ink)]/80 leading-relaxed max-w-2xl">
              <p>
                <span className="font-semibold text-[color:var(--brand-forest)]">Bawajii Travels &amp; Infra</span> is a family-run travel house from Pal, Surat.
                What began as a single car serving neighbours grew — one recommendation at a time — into a curated fleet of sedans, SUVs and tempo travellers
                serving Gujarat, Maharashtra, Rajasthan and beyond.
              </p>
              <p>
                We are not an app that dispatches strangers. Every car we send out is inspected, every chauffeur is vetted, and every fare is quoted before you sit in.
                From a school run in Pal to a week-long Rajasthan trail, our promise stays the same.
              </p>
              <blockquote className="border-l-4 border-[color:var(--brand-amber)] pl-5 py-2 mt-2">
                <p className="font-display text-2xl md:text-3xl text-[color:var(--brand-forest)]">
                  Comfortable · Reasonable · Reliable
                </p>
                <p className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)] mt-2">— our quote, our commitment</p>
              </blockquote>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="clay-card rounded-2xl p-6" data-testid="about-stat-years">
                <div className="font-display text-4xl text-[color:var(--brand-forest)]">15+</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)] mt-1">Years on the road</div>
              </div>
              <div className="clay-card rounded-2xl p-6" data-testid="about-stat-rides">
                <div className="font-display text-4xl text-[color:var(--brand-forest)]">50K+</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)] mt-1">Trips completed</div>
              </div>
              <div className="clay-card rounded-2xl p-6" data-testid="about-stat-cars">
                <div className="font-display text-4xl text-[color:var(--brand-forest)]">30+</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)] mt-1">Cars in fleet</div>
              </div>
              <div className="clay-card rounded-2xl p-6" data-testid="about-stat-support">
                <div className="font-display text-4xl text-[color:var(--brand-forest)]">24×7</div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)] mt-1">On-call support</div>
              </div>
            </div>
            <div className="mt-6 clay-card rounded-2xl p-6" data-testid="about-values">
              <div className="text-xs uppercase tracking-widest text-[color:var(--brand-amber)]">What we live by</div>
              <ul className="mt-3 space-y-2 text-sm text-[color:var(--brand-ink)]/85">
                <li><span className="font-semibold text-[color:var(--brand-forest)]">Comfortable</span> — spotless cars, AC always on, water on long trips.</li>
                <li><span className="font-semibold text-[color:var(--brand-forest)]">Reasonable</span> — one clean quote. No surge, no surprises.</li>
                <li><span className="font-semibold text-[color:var(--brand-forest)]">Reliable</span> — drivers on time, phones answered, plans honoured.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
        <div className="grid gap-4 md:grid-cols-12 items-end mb-14">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--brand-amber)]">What we offer</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 max-w-2xl leading-tight">Comfortable travel, thoughtfully arranged.</h2>
          </div>
          <p className="md:col-span-4 text-[color:var(--brand-muted)]">Three ways to travel. All with the same promise — clean cars, courteous drivers, and clear pricing.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <div key={s.title} data-testid={`service-card-${i}`} className="clay-card rounded-2xl p-8 fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="h-12 w-12 rounded-full bg-[color:var(--brand-forest)]/10 grid place-items-center text-[color:var(--brand-forest)]">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-2xl mt-5">{s.title}</h3>
              <p className="mt-3 text-[color:var(--brand-muted)] leading-relaxed">{s.desc}</p>
              <div className="mt-5 text-xs uppercase tracking-widest text-[color:var(--brand-amber)]">{s.tag}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="bg-[color:var(--brand-forest)] text-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid md:grid-cols-12 items-end mb-12">
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--brand-amber)]">Our fleet</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3 max-w-2xl">Choose a car that fits the moment.</h2>
            </div>
            <p className="md:col-span-4 text-white/70">Cars are inspected before every trip. Fuel and driver bhatta included in one clean quote.</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {FLEET.map((f, i) => (
              <div key={f.name} data-testid={`fleet-card-${i}`} className="group fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="overflow-hidden rounded-2xl">
                  <img src={f.img} alt={f.name} className="h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
                <div className="mt-4">
                  <h3 className="font-display text-2xl">{f.name}</h3>
                  <div className="text-sm text-white/60 mt-1">{f.capacity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section id="routes" className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-12 items-end mb-12">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--brand-amber)]">From Surat · Popular routes</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Weekend getaways &amp; outstation trips.</h2>
            <p className="mt-4 text-[color:var(--brand-muted)] max-w-2xl">
              Tap any route to pre-fill the booking form with Surat as your pickup. Our team will confirm your quote on WhatsApp based on your date, vehicle and stops.
            </p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ROUTES.map((r, i) => (
            <button
              key={r.to}
              type="button"
              onClick={() => bookRoute(r.to)}
              data-testid={`route-card-${i}`}
              className="group text-left flex flex-col p-6 clay-card rounded-xl hover:-translate-y-1 transition-transform duration-300"
            >
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand-muted)]">Surat →</div>
                <div className="font-display text-xl mt-1">{r.to}</div>
                <div className="text-xs text-[color:var(--brand-muted)] mt-1">Approx. {r.time} · {r.km}</div>
              </div>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--brand-forest)] group-hover:text-[color:var(--brand-amber)] transition-colors duration-300">
                Book this route <ArrowRight className="h-4 w-4" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[color:var(--brand-cream)] py-24 border-y border-black/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-4 md:grid-cols-12 mb-12">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--brand-amber)]">Why Bawajii</div>
              <h2 className="font-display text-4xl md:text-5xl mt-3">Small details. Big difference.</h2>
            </div>
          </div>
          <div className="grid gap-8 md:grid-cols-4">
            {TRUST.map((t, i) => (
              <div key={t.title} data-testid={`trust-${i}`} className="fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="h-10 w-10 rounded-full bg-[color:var(--brand-forest)]/10 grid place-items-center text-[color:var(--brand-forest)]">
                  <t.icon className="h-5 w-5" />
                </div>
                <h4 className="font-display text-xl mt-4">{t.title}</h4>
                <p className="text-sm text-[color:var(--brand-muted)] mt-2 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Home;
